import { collection } from 'typesaurus'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Timestamp } from '@google-cloud/firestore'

export interface AnnouncementType {
  user:string
  message:string
  created:string
  comments?:MessageType[]
}

export interface ApiType {
  type:string // infusionsoft, tag
  value:any
}

export interface BlackoutDateType {
  id?: string
  date: string
  location:string
}

export interface CommentType {
  user:string
  comment:string
  created:any
  shared?:boolean
}
export interface CouponTemplateType {
  id?:string
  name:string
  value: string
  internalValue: number
  details: string
  notes: string
  comments: CommentType[]
  expires: string
  group: string
  created: Date
  singleLocation: string
  type:string,
  categories: string[] // couponCategory ID
  creationApis: ApiType[],
  redemptionApis: ApiType[],
  creationTags: string[]
  redemptionTags: string[]
}

export interface CouponType {
  value: string
  internalValue: number
  details: string
  notes: string
  comments: CommentType[]
  expiration: string
  group: string
  singleLocation: string
  creationApis: ApiType[],
  redemptionApis: ApiType[],
  creationTags: string[]
  redemptionTags: string[]
  codes?: string[],
  redeemed: any
  customer: string // customer ID
  categories: string[] // couponCategory ID
  spend: number|null
  redemptionNotes: string
  redeemedBy: string
  created:any
}

export interface BuildCouponType {
  value: string
  internalValue: number
  details: string
  notes: string
  type?:string
  name?:string
  expiration: string
  expires?:string
  singleLocation: string
  creationApis: ApiType[],
  redemptionApis: ApiType[],
  creationTags: string[],
  redemptionTags: string[],
  customer?: string // customer ID
  categories: string[] // couponCategory ID
}

export interface CustomerType {
  id?: string,
  birthday: Date|null,
  created: Date,
  email: string,
  firstName: string,
  lastName: string,
  infusionsoftId: string,
  comments: CommentType[],
  phone: string,
  profile: string,
  tags: string[], // tag ID
  updated: Date
}

export interface DisplayBoardType {
  id?:string
  name:string
  description:string
  landscape:boolean
  startHour: number
  created:Date
  slides: string[]
  text:any[]
  settings:any
  location:string
}


export interface DisplaySlideType {
  id?:string
  active:boolean
  name:string
  type:string // image, video, dynamic
  landscape:boolean
  url?:string // for video or image
  transition:string
  component?:string
  location?:string
  boards:string[]
  created: Date
  updated: Date
  background:string
}

export interface SlideDetailsType {
  id?:string
  slide:string
  startDate: Date|null
  endDate: Date|null
  duration:number
  board: string
  schedule:{start:number, end:number}[]
}

export interface InfoBoardType {
  id?: string
  name: string
  weekday?: number
  text:string
  location?:string
  description:string
  category:string
  updated:Date
  updatedBy?:string
}

export interface LocationListType {
  name: string
  avatar: string
  id: string
}

export interface LocationType {
  id?:string
  avatar:string
  blackoutDates: string[]
  canvasEmail:string
  canvasPhone:string
  couponMap?:object
  email:string
  group:string
  managers:string[]
  masterTag:string
  name:string
  phone:string
  slackId:string
  timezone:string
  checkNames:string[]
}

export interface MessageType {
  user:string
  message:string
  created:string
  comments?: CommentType[]
}

export interface NotificationType {
  user:string
  location:string
  title:string
  message:string
  expires:string
  push:boolean
  created:Date|Timestamp
  comments?: CommentType[]
}

export interface RequestOffType {
  id?: string
  user: string
  start: string
  end: string
  reason: string
  location:string
  status: string
  notes: string
  created: Date
  updated?: Date
  manager?: string
}

export interface TaskType {
  id?: string
  employee:string
  shift:boolean
  location:string
  created:Date
  due:string
  task:string
  details:string
  completed:Date|null
  completedBy: string
  comments: CommentType[]
}

export interface UserNoteType {
  user:string
  created: Date
  location:string
  group:string
  manager:string
  note:string
}

export interface UserType {
  id: string
  created: Date
  firstName: string
  lastName: string
  email: string
  phone: string
  multi: boolean
  locations: string[] // Location ID
  locationList?: LocationListType[]
  authTags: string[]
}

export interface RdbUserType {
  id:string,
  lastOnlineChanged:string,
  online:boolean
  '.key'?:string
}

// Reports
export interface WrittenCheckType {
  user:string
  date:string
  to: string
  total?:number
  checkNumber:string
  invoiceNumber:string
  note:string
  created?: Date
  createdBy?: string
}

// ---------------------------------------------------------
// ---------------------------       Report Types Here   ---
// ---------------------------------------------------------

export interface ServerReportType {
  user:string
  shift:string
  date:string
  sales:number|null
  comps:CompVoidType[]
  voids:CompVoidType[]
  foodTimingRating:number
  foodQualityRating:number
  teamworkRating:number
  overallRating:number
  incidents:string
  comments:string
  created?: Date
  createdBy?: string
}

export interface IncidentReportType {
  user:string
  employee:string
  datetime: Date
  type:string
  priority:string
  description:string
  resolution:string
  customer?: string
  created?: Date
  createdBy?: string
}

export interface ManagerReportType {
  user:string
  shift:string
  date:string
  foh:string
  employees: EmployeeReviewType[]
  boh:string
  complaints:string
  notes:string
  created?: Date
  createdBy?: string
}

export interface NewMessageType {
  user:string
  to:string
  datetime:Date
  contact:SmallContactType
  type:string
  priority:string
  message:string
  created?: Date
  createdBy?: string
}

type CompVoidType = {
  checkNumber:string
  amount:number
  explaination:string
}

export interface CommentCardType {
  user:string
  date:string
  shift:string
  employee:string
  contact: {
    firstName:string
    lastName:string
    email:string
    phone:string
  }
  foodRating:number
  serviceRating:number
  note:string
}

type EmployeeReviewType = {
  user:string
  rating:number
  note:string
}

type SmallContactType = {
  name: string
  email: string
  phone:string
}

type SlackType = {
  channel:string
  test:Function
  template:string
}

// PP CONTAINS SPACES


// Collections Here

export const DisplayBoardCollection = collection<DisplayBoardType>('displayBoards')

export const DisplaySlideCollection = collection<DisplaySlideType>('displaySlides')

export const LocationCollection = collection<LocationType>('locations')

export const SlideDetailsCollection = collection<SlideDetailsType>('displaySlideDetails')

export const UserCollection = collection<UserType>('users')

export const UserNotesCollection = collection<UserNoteType>('userNotes')

export const NotificationCollection = collection<NotificationType>('notifications')
